import React, { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Loader2, AlertCircle, ImagePlus, X } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { usePageTitle } from "@/hooks/usePageTitle";
import { submitFeedback } from "@/functions/submitFeedback";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { value: "bug", label: "Bug — something is broken" },
  { value: "confusing", label: "Confusing — unclear or hard to use" },
  { value: "idea", label: "Idea — a suggestion" },
  { value: "praise", label: "Praise — something you like" },
];

const SEVERITIES = [
  { value: "low", label: "Low — minor / cosmetic" },
  { value: "medium", label: "Medium — feature partly broken" },
  { value: "high", label: "High — blocks me from working" },
];

const BUG_TITLE_MIN = 5;
const BUG_TITLE_MAX = 100;
const BUG_DESCRIPTION_MIN = 30;
const NON_BUG_DESCRIPTION_MIN = 5;
const DESCRIPTION_MAX = 2000;

function inferReferrerPage() {
  if (typeof window === "undefined") return "";
  try {
    const fromParam = new URLSearchParams(window.location.search).get("from");
    if (fromParam) return fromParam;
  } catch {
    // ignore
  }
  try {
    if (document.referrer) {
      const ref = new URL(document.referrer);
      if (ref.origin === window.location.origin) {
        return ref.pathname + ref.search;
      }
    }
  } catch {
    // ignore
  }
  return "";
}

export default function Feedback() {
  usePageTitle("Send Feedback");
  const navigate = useNavigate();

  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [pageAffected, setPageAffected] = useState(() => inferReferrerPage());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [screenshotName, setScreenshotName] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setScreenshotUrl(file_url);
      setScreenshotName(file.name);
    } catch (err) {
      toast.error("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
      // reset input so the same file can be re-selected after removal
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeScreenshot = () => {
    setScreenshotUrl(null);
    setScreenshotName(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isBug = type === "bug";
  const showSeverity = isBug;
  const showPageAffected = isBug || type === "confusing";

  const descriptionMin = isBug ? BUG_DESCRIPTION_MIN : NON_BUG_DESCRIPTION_MIN;
  const descriptionPlaceholder = isBug
    ? `What were you trying to do? What happened instead? Steps to reproduce. (min ${BUG_DESCRIPTION_MIN} chars)`
    : "Tell us what's on your mind.";

  const titleError = useMemo(() => {
    if (!isBug) return null;
    if (!title) return null;
    if (title.length < BUG_TITLE_MIN) return `Title must be at least ${BUG_TITLE_MIN} characters.`;
    if (title.length > BUG_TITLE_MAX) return `Title must be at most ${BUG_TITLE_MAX} characters.`;
    return null;
  }, [title, isBug]);

  const descriptionError = useMemo(() => {
    if (!description) return null;
    if (description.length < descriptionMin) {
      return `Please add at least ${descriptionMin} characters.`;
    }
    if (description.length > DESCRIPTION_MAX) {
      return `Description must be at most ${DESCRIPTION_MAX} characters.`;
    }
    return null;
  }, [description, descriptionMin]);

  const isValid = useMemo(() => {
    const descOk =
      description.length >= descriptionMin &&
      description.length <= DESCRIPTION_MAX;
    if (!descOk) return false;
    if (isBug) {
      return (
        title.length >= BUG_TITLE_MIN &&
        title.length <= BUG_TITLE_MAX
      );
    }
    return true;
  }, [description, descriptionMin, title, isBug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const { error: fnError } = await submitFeedback({
        type,
        title: title.trim(),
        description: description.trim(),
        severity,
        page_affected: showPageAffected ? pageAffected || "" : "",
        screenshot_url: screenshotUrl || undefined,
      });

      if (fnError) {
        throw new Error(fnError.message || "Failed to submit feedback");
      }

      toast.success("Thanks — got it!");
      setTitle("");
      setDescription("");
      setSeverity("medium");
      setScreenshotUrl(null);
      setScreenshotName(null);
      // Keep the inferred page affected; reset type to bug so required fields reset sensibly.
      setType("bug");
    } catch (err) {
      logger.error(err, { context: "Feedback submit" });
      setError(err.message || "Something went wrong. Please try again.");
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-cyan-300" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Send Feedback</h1>
          <p className="text-sm text-[#94A3B8]">
            Bugs, confusing moments, ideas, or just praise — tell us anything.
            You're one of a handful of alpha testers and your input shapes what
            we build.
          </p>
        </div>
      </div>

      <Card className="glass border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-lg">What's on your mind?</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="feedback-type" className="text-white">
                Type <span className="text-[#EF4444]">*</span>
              </Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger
                  id="feedback-type"
                  className="bg-[#0F172A]/60 border-white/10 text-white"
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="glass border-white/10 text-white">
                  {TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-title" className="text-white">
                Title{" "}
                {isBug && <span className="text-[#EF4444]">*</span>}
              </Label>
              <Input
                id="feedback-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={BUG_TITLE_MAX}
                placeholder="One-line summary"
                aria-invalid={!!titleError}
                aria-describedby={titleError ? "feedback-title-error" : undefined}
                className="bg-[#0F172A]/60 border-white/10 text-white placeholder:text-[#64748B]"
                required={isBug}
              />
              <div className="flex justify-between text-xs">
                <span id="feedback-title-error" className="text-amber-300">
                  {titleError}
                </span>
                <span className="text-[#64748B]">
                  {title.length}/{BUG_TITLE_MAX}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-description" className="text-white">
                Description <span className="text-[#EF4444]">*</span>
              </Label>
              <Textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={DESCRIPTION_MAX}
                rows={6}
                placeholder={descriptionPlaceholder}
                aria-invalid={!!descriptionError}
                aria-describedby={descriptionError ? "feedback-description-error" : undefined}
                className="bg-[#0F172A]/60 border-white/10 text-white placeholder:text-[#64748B]"
                required
              />
              <div className="flex justify-between text-xs">
                <span id="feedback-description-error" className="text-amber-300">
                  {descriptionError}
                </span>
                <span className="text-[#64748B]">
                  {description.length}/{DESCRIPTION_MAX}
                </span>
              </div>
            </div>

            {/* Screenshot upload — optional for all types */}
            <div className="space-y-2">
              <Label className="text-white">Screenshot <span className="text-[#64748B] font-normal">(optional)</span></Label>
              <p className="text-xs text-[#64748B]">
                Attach an image if it helps — especially for visual bugs.
              </p>
              {screenshotUrl ? (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#0F172A]/60 border border-white/10">
                  <img
                    src={screenshotUrl}
                    alt="Screenshot preview"
                    className="w-12 h-12 object-cover rounded-md border border-white/10 flex-shrink-0"
                  />
                  <span className="text-sm text-[#94A3B8] truncate flex-1">{screenshotName}</span>
                  <button
                    type="button"
                    onClick={removeScreenshot}
                    aria-label="Remove screenshot"
                    className="text-[#64748B] hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-white/20 text-[#94A3B8] hover:border-cyan-500/50 hover:text-cyan-300 transition-colors text-sm disabled:opacity-50"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                  ) : (
                    <><ImagePlus className="w-4 h-4" /> Choose image</>
                  )}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {showSeverity && (
              <div className="space-y-2">
                <Label htmlFor="feedback-severity" className="text-white">
                  Severity
                </Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger
                    id="feedback-severity"
                    className="bg-[#0F172A]/60 border-white/10 text-white"
                  >
                    <SelectValue placeholder="Select severity" />
                  </SelectTrigger>
                  <SelectContent className="glass border-white/10 text-white">
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {showPageAffected && (
              <div className="space-y-2">
                <Label htmlFor="feedback-page" className="text-white">
                  Page or feature affected
                </Label>
                <Input
                  id="feedback-page"
                  value={pageAffected}
                  onChange={(e) => setPageAffected(e.target.value)}
                  placeholder="e.g. /Generate, AI Avatars, Marketplace listing modal"
                  className="bg-[#0F172A]/60 border-white/10 text-white placeholder:text-[#64748B]"
                />
                <p className="text-xs text-[#64748B]">
                  Pre-filled from where you came from when possible.
                </p>
              </div>
            )}

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={!isValid || submitting}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold h-11"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send feedback
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}